import * as Sentry from "@sentry/react";
import type {
  AccessToken,
  Features,
  Model,
  PlaygroundPermissions,
  Prediction,
  Version,
} from "../../types";
import { PlaygroundContext, type PlaygroundContextValue } from "./context";
import PredictionOutput from "./prediction-output";
import { getRenderMode } from "./util";

export default function PredictionReportOutput({
  features,
  isAuthenticated,
  model,
  permissions,
  prediction,
  requiresCredit,
  token,
  usesVersionlessApi,
  version,
}: {
  features: Features;
  isAuthenticated: boolean;
  model: Model;
  permissions: PlaygroundPermissions;
  prediction: Prediction | null | undefined;
  requiresCredit: boolean;
  token?: AccessToken | null;
} & (
  | {
      usesVersionlessApi: true;
      version: Version | null;
    }
  | {
      usesVersionlessApi: false;
      version: Version;
    }
)) {
  if (!prediction) {
    Sentry.captureEvent({
      message: "Somehow reporting a prediction's output without a prediction.",
      level: "error",
    });
    return null;
  }

  const context: PlaygroundContextValue = {
    elementVisibility: {
      tweakButton: false,
    },
    features,
    hideAdvancedInputs: false,
    isAuthenticated,
    model: model,
    modelInputSettings: { hidden: [] },
    modelStatus: null,
    permissions,
    requiresCredit,
    renderMode: getRenderMode({ features }),
    token: token ?? null,
    ...(usesVersionlessApi
      ? {
          usesVersionlessApi: true,
          version,
        }
      : {
          usesVersionlessApi: false,
          version,
        }),
  };

  return (
    <PlaygroundContext.Provider value={context}>
      <PredictionOutput
        alwaysRenderURLsAsDownload={false}
        prediction={prediction}
        shouldAutoScroll={false}
      />
    </PlaygroundContext.Provider>
  );
}
